/**
 * Consignado por cliente — todo lo que un cliente x mayor tiene en
 * consignación, sin importar en qué conduce se lo llevó.
 *
 * El cliente reporta "vendí estos ocho" sin saber de qué entrega salió cada
 * uno. Aquí se eligen (todos, por conduce o escaneando el chasis) y en un solo
 * paso quedan vendidos y facturados: los que estaban en piso se marcan
 * vendidos con la fecha indicada y los ya marcados conservan la suya.
 *
 * Sale una factura por cada empresa y moneda de los conduces involucrados (una
 * factura lleva una sola de cada una); todas comparten comprobante, condición,
 * plazos y la tasa de hoy. El backend valida el crédito contra la operación
 * completa y vuelve a validar precios mínimos (conducexmayor_facturarcliente).
 *
 * Permisos: ver exige el programa conducesxmayor; facturar, su "modificar".
 */
import React, { useState, useEffect, useMemo } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Chip from "@mui/material/Chip";
import Checkbox from "@mui/material/Checkbox";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import CircularProgress from "@mui/material/CircularProgress";
import InputAdornment from "@mui/material/InputAdornment";
import { DataGrid } from "@mui/x-data-grid";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SearchIcon from "@mui/icons-material/Search";
import QrCodeScannerIcon from "@mui/icons-material/QrCodeScanner";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { useGlobalMDAlert, useMaterialUIController } from "context";
import { FP, StatusPill, fpInputSx, fpDataGridSx } from "components/financepro";
import { puedeAccionPrograma } from "lib/seguridad/permisosPorModulo";
import { useApi } from "lib/api";
import { precioEnMoneda } from "lib/moneda";
import FacturaDetalleXmayor from "modules/ventasxmayor/facturadetallexmayor";
import { fmtMoney, monedaLabel } from "modules/ventasxmayor/impresionxmayor";

const toNum = (v) => {
  const n = parseFloat(String(v || "").replace(/,/g, ""));
  return Number.isNaN(n) ? 0 : n;
};

const round2 = (n) => Math.round(n * 100) / 100;

const fmtFechaISO = (d) => {
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

const hoyISO = () => fmtFechaISO(new Date());

const hoyMasDias = (dias) => {
  const d = new Date();
  d.setDate(d.getDate() + dias);
  return fmtFechaISO(d);
};

// El lector con el teclado en otro idioma manda "*" donde va el guion.
const normalizarChasis = (v) =>
  String(v || "")
    .trim()
    .replace(/\*/g, "-")
    .toUpperCase();

const btnBase = { textTransform: "none", borderRadius: 1.8, fontWeight: 700, boxShadow: "none" };
const btnSolid = (bg, hover) => ({
  ...btnBase,
  bgcolor: bg,
  color: "#FFFFFF",
  "&:hover": { bgcolor: hover, boxShadow: "none", color: "#FFFFFF" },
  "&.Mui-disabled": { bgcolor: `${bg}66`, color: "#FFFFFF" },
});
const btnOutline = (c) => ({
  ...btnBase,
  color: c,
  bgcolor: "#FFFFFF",
  border: `1px solid ${c}`,
  "&:hover": { bgcolor: `${c}12`, border: `1px solid ${c}`, color: c },
});
const btnGhost = {
  ...btnBase,
  color: FP.color.textSecondary,
  bgcolor: "transparent",
  "&:hover": { bgcolor: FP.color.surfaceAlt, color: FP.color.textPrimary },
};

const sectionBox = {
  border: `1px solid ${FP.color.border}`,
  borderRadius: 2.5,
  p: { xs: 2, sm: 2.6 },
  mb: 2.4,
  bgcolor: FP.color.cardBg,
  boxShadow: FP.shadow.soft,
};

function SinFilas({ texto }) {
  return (
    <Stack
      alignItems="center"
      justifyContent="center"
      sx={{ height: "100%", py: 6, color: FP.color.textMuted }}
    >
      <InboxOutlinedIcon sx={{ fontSize: 44, mb: 1 }} />
      <Typography sx={{ fontWeight: 700, color: FP.color.textSecondary }}>{texto}</Typography>
    </Stack>
  );
}

function SinClientes() {
  return <SinFilas texto="Ningún cliente tiene mercancía en consignación" />;
}

function SinLineas() {
  return <SinFilas texto="Nada que coincida con el filtro" />;
}

const claveGrupo = (l) => `${l.empresa || 1}|${l.moneda === "U" ? "U" : "P"}`;

export default function ConsignadoPorCliente({ refreshToken = 0, onChanged } = {}) {
  const { showAlert } = useGlobalMDAlert();
  const { post } = useApi();
  const [controller] = useMaterialUIController();
  const { roleDetalles } = controller;
  const puedeModificar = puedeAccionPrograma(roleDetalles, "conducesxmayor", "modificar");

  const [config, setConfig] = useState(null);
  useEffect(() => {
    post("/api/xmayor_config", {}).then(({ ok, data }) => {
      if (ok) setConfig(data);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- Resumen: clientes con mercancía en consignación ----
  const [clientes, setClientes] = useState([]);
  const [cargandoClientes, setCargandoClientes] = useState(false);
  const [filtroCliente, setFiltroCliente] = useState("");

  const cargarClientes = async () => {
    setCargandoClientes(true);
    try {
      const { ok, data } = await post("/api/conducexmayor_pendientescliente", {});
      if (ok) {
        setClientes(data.clientes || []);
      } else {
        showAlert({ message: data.Error || "No se pudo cargar lo consignado", color: "error" });
      }
    } finally {
      setCargandoClientes(false);
    }
  };

  // ---- Cliente elegido: sus líneas de todos los conduces ----
  const [cliente, setCliente] = useState(null);
  const [lineas, setLineas] = useState([]);
  const [cargandoLineas, setCargandoLineas] = useState(false);
  const [seleccion, setSeleccion] = useState([]);
  const [filtroConduce, setFiltroConduce] = useState(0);
  const [busqueda, setBusqueda] = useState("");

  const cargarLineas = async (cli = cliente) => {
    if (!cli) return;
    setCargandoLineas(true);
    try {
      const { ok, data } = await post("/api/conducexmayor_pendientescliente", {
        cliente: cli.cliente,
      });
      if (ok) {
        setLineas(data.lineas || []);
        setSeleccion([]);
      } else {
        showAlert({ message: data.Error || "No se pudo cargar el cliente", color: "error" });
      }
    } finally {
      setCargandoLineas(false);
    }
  };

  useEffect(() => {
    cargarClientes();
    if (cliente) cargarLineas();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshToken]);

  const abrirCliente = (cli) => {
    setCliente(cli);
    setLineas([]);
    setSeleccion([]);
    setFiltroConduce(0);
    setBusqueda("");
    cargarLineas(cli);
  };

  const volver = () => {
    setCliente(null);
    setLineas([]);
    setSeleccion([]);
    cargarClientes();
  };

  const conduces = useMemo(() => {
    const m = new Map();
    lineas.forEach((l) => {
      const c = m.get(l.conduce) || { id: l.conduce, fecha: l.fechaconduce, motores: 0 };
      c.motores += 1;
      m.set(l.conduce, c);
    });
    return [...m.values()];
  }, [lineas]);

  const variasEmpresas = useMemo(
    () => new Set(lineas.map((l) => l.empresa || 1)).size > 1,
    [lineas]
  );

  const visibles = useMemo(() => {
    const q = normalizarChasis(busqueda);
    return lineas.filter(
      (l) =>
        (!filtroConduce || l.conduce === filtroConduce) &&
        (!q || String(l.chasis).toUpperCase().includes(q))
    );
  }, [lineas, filtroConduce, busqueda]);

  const todosVisiblesMarcados =
    visibles.length > 0 && visibles.every((l) => seleccion.includes(l.chasis));

  const alternarVisibles = () => {
    const chs = visibles.map((l) => l.chasis);
    setSeleccion((prev) =>
      todosVisiblesMarcados
        ? prev.filter((x) => !chs.includes(x))
        : [...prev, ...chs.filter((x) => !prev.includes(x))]
    );
  };

  const alternar = (chasis) =>
    setSeleccion((prev) =>
      prev.includes(chasis) ? prev.filter((x) => x !== chasis) : [...prev, chasis]
    );

  // Enter en la búsqueda (o el lector, que termina en Enter): si el chasis
  // coincide exacto se agrega a la selección y el campo queda listo para el
  // siguiente.
  const alEnter = () => {
    const q = normalizarChasis(busqueda);
    if (!q) return;
    const l = lineas.find((x) => String(x.chasis).toUpperCase() === q);
    if (!l) {
      showAlert({ message: `El chasis ${q} no está consignado a este cliente`, color: "warning" });
      return;
    }
    setSeleccion((prev) => (prev.includes(l.chasis) ? prev : [...prev, l.chasis]));
    setBusqueda("");
  };

  const seleccionadas = useMemo(
    () => lineas.filter((l) => seleccion.includes(l.chasis)),
    [lineas, seleccion]
  );
  const selEnPiso = seleccionadas.filter((l) => !String(l.estado || "").trim()).length;
  const enPisoTotal = lineas.filter((l) => !String(l.estado || "").trim()).length;
  const vendidosTotal = lineas.length - enPisoTotal;

  // ---- Vender y facturar ----
  const [factOpen, setFactOpen] = useState(false);
  const [factPrecios, setFactPrecios] = useState({});
  const [fechaventa, setFechaventa] = useState(hoyISO());
  const [tipocomprobante, setTipocomprobante] = useState("");
  const [tipofactura, setTipofactura] = useState("Credito");
  const [plazos, setPlazos] = useState("1");
  const [fechaprimerpago, setFechaprimerpago] = useState(hoyMasDias(30));
  const [tasa, setTasa] = useState("");
  const [facturando, setFacturando] = useState(false);
  const [resultado, setResultado] = useState(null);
  const [facturaId, setFacturaId] = useState(null);

  const abrirFacturar = () => {
    setFactPrecios(
      Object.fromEntries(seleccionadas.map((l) => [l.chasis, toNum(l.precio).toFixed(2)]))
    );
    // Como en el detalle del conduce: la del conduce (aquí el más reciente de
    // la selección), editable porque la factura es de hoy.
    const reciente = seleccionadas.reduce((a, l) => (!a || l.conduce > a.conduce ? l : a), null);
    setTasa(String(toNum(reciente?.tasa) || config?.tasaus || ""));
    setFechaventa(hoyISO());
    setTipocomprobante(config?.tiposComprobante?.[0] || "");
    setTipofactura("Credito");
    setPlazos("1");
    setFechaprimerpago(hoyMasDias(30));
    setFactOpen(true);
  };

  const minimo = (l) => precioEnMoneda(l.mprecio, l.moneda, tasa, config?.base || "DOP");

  // Una factura por empresa y moneda, en el orden en que aparecen (el mismo
  // que usa el backend).
  const grupos = useMemo(() => {
    const m = new Map();
    seleccionadas.forEach((l) => {
      const k = claveGrupo(l);
      if (!m.has(k)) {
        m.set(k, {
          clave: k,
          empresa: l.empresa || 1,
          moneda: l.moneda === "U" ? "U" : "P",
          lineas: [],
        });
      }
      m.get(k).lineas.push(l);
    });
    return [...m.values()].map((g) => ({
      ...g,
      total: round2(g.lineas.reduce((a, l) => a + toNum(factPrecios[l.chasis]), 0)),
      conduces: [...new Set(g.lineas.map((l) => l.conduce))].sort((a, b) => a - b),
    }));
  }, [seleccionadas, factPrecios]);

  const bajoMinimo = useMemo(
    () => seleccionadas.find((l) => minimo(l) > 0 && toNum(factPrecios[l.chasis]) < minimo(l)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [seleccionadas, factPrecios, tasa, config]
  );

  const nPlazos = parseInt(plazos, 10) || 0;
  const plazosValidos = tipofactura !== "Credito" || (nPlazos >= 1 && nPlazos <= 60);

  const facturar = async () => {
    if (bajoMinimo) {
      showAlert({
        message: `El precio del chasis ${
          bajoMinimo.chasis
        } no puede ser menor al mínimo (${monedaLabel(bajoMinimo.moneda)} ${fmtMoney(
          minimo(bajoMinimo)
        )})`,
        color: "warning",
      });
      return;
    }
    setFacturando(true);
    try {
      const { ok, data } = await post("/api/conducexmayor_facturarcliente", {
        cliente: cliente.cliente,
        chasis: seleccionadas.map((l) => l.chasis),
        items: seleccionadas.map((l) => ({
          chasis: l.chasis,
          precio: toNum(factPrecios[l.chasis]),
        })),
        fechaventa,
        tipocomprobante,
        tipofactura,
        tasa: toNum(tasa),
        plazos: tipofactura === "Credito" ? nPlazos || 1 : 1,
        fechaprimerpago,
      });
      if (ok) {
        showAlert({ message: data.ok || "Factura registrada", color: "success" });
        setFactOpen(false);
        setResultado(data);
        cargarLineas();
        cargarClientes();
        if (onChanged) onChanged();
      } else {
        showAlert({ message: data.Error || "No se pudo facturar", color: "error" });
      }
    } finally {
      setFacturando(false);
    }
  };

  // ---- Columnas ----
  const columnasClientes = [
    { field: "clientenombre", headerName: "Cliente", flex: 1, minWidth: 200 },
    {
      field: "conduces",
      headerName: "Conduces",
      width: 100,
      align: "center",
      headerAlign: "center",
    },
    { field: "enpiso", headerName: "En piso", width: 100, align: "center", headerAlign: "center" },
    {
      field: "vendidos",
      headerName: "Por facturar",
      width: 120,
      align: "center",
      headerAlign: "center",
      renderCell: (p) =>
        p.value > 0 ? <StatusPill label={String(p.value)} variant="warning" /> : 0,
    },
    { field: "desde", headerName: "Desde", width: 110 },
    {
      field: "monedas",
      headerName: "Moneda",
      width: 110,
      valueFormatter: (v) =>
        String(v || "P")
          .split(",")
          .map(monedaLabel)
          .join(" / "),
    },
    {
      field: "abrir",
      headerName: "",
      width: 120,
      sortable: false,
      filterable: false,
      renderCell: (p) => (
        <Button
          size="small"
          startIcon={<VisibilityOutlinedIcon />}
          onClick={() => abrirCliente(p.row)}
          sx={btnOutline(FP.color.accentBlue)}
        >
          Abrir
        </Button>
      ),
    },
  ];

  const columnasLineas = [
    ...(puedeModificar
      ? [
          {
            field: "sel",
            headerName: "",
            width: 56,
            sortable: false,
            filterable: false,
            renderHeader: () => (
              <Checkbox
                size="small"
                checked={todosVisiblesMarcados}
                indeterminate={
                  !todosVisiblesMarcados && visibles.some((l) => seleccion.includes(l.chasis))
                }
                onChange={alternarVisibles}
              />
            ),
            renderCell: (p) => (
              <Checkbox
                size="small"
                checked={seleccion.includes(p.row.chasis)}
                onChange={() => alternar(p.row.chasis)}
              />
            ),
          },
        ]
      : []),
    {
      field: "conduce",
      headerName: "Conduce",
      width: 120,
      renderCell: (p) => (
        <Box sx={{ lineHeight: 1.2 }}>
          <Typography sx={{ fontSize: "0.82rem", fontWeight: 700 }}>#{p.row.conduce}</Typography>
          <Typography sx={{ fontSize: "0.68rem", color: FP.color.textMuted }}>
            {p.row.fechaconduce}
          </Typography>
        </Box>
      ),
    },
    { field: "chasis", headerName: "Chasis", width: 180 },
    { field: "marca", headerName: "Marca", width: 110 },
    { field: "modelo", headerName: "Modelo", width: 120 },
    { field: "colore", headerName: "Color", width: 90 },
    {
      field: "estado",
      headerName: "Estado",
      width: 150,
      renderCell: (p) =>
        p.row.estado === "V" ? (
          <StatusPill
            label={p.row.fechaventa ? `Vendido ${p.row.fechaventa}` : "Vendido"}
            variant="warning"
          />
        ) : (
          <StatusPill label="En piso" variant="info" />
        ),
    },
    {
      field: "precio",
      headerName: "Pactado",
      width: 140,
      align: "right",
      headerAlign: "right",
      valueFormatter: (v, row) => `${monedaLabel(row.moneda)} ${fmtMoney(v)}`,
    },
    ...(variasEmpresas
      ? [
          {
            field: "empresa",
            headerName: "Empresa",
            width: 90,
            align: "center",
            headerAlign: "center",
          },
        ]
      : []),
    { field: "dias", headerName: "Días", width: 80, align: "center", headerAlign: "center" },
  ];

  // ---- Vista: resumen de clientes ----
  if (!cliente) {
    const q = filtroCliente.trim().toLowerCase();
    const filas = q
      ? clientes.filter((c) => String(c.clientenombre).toLowerCase().includes(q))
      : clientes;
    return (
      <Box sx={{ ...sectionBox, mb: 0 }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems={{ sm: "center" }}
          sx={{ mb: 2 }}
        >
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontWeight: 800, fontSize: "0.95rem" }}>
              Mercancía en consignación por cliente
            </Typography>
            <Typography sx={{ fontSize: "0.78rem", color: FP.color.textSecondary }}>
              Abra un cliente para marcar vendidos y facturar chasis de cualquiera de sus conduces
              en un solo paso.
            </Typography>
          </Box>
          <TextField
            size="small"
            placeholder="Buscar cliente"
            value={filtroCliente}
            onChange={(e) => setFiltroCliente(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ ...fpInputSx, minWidth: 240 }}
          />
        </Stack>
        <Box sx={{ bgcolor: "#fff" }}>
          <DataGrid
            autoHeight
            rows={filas}
            columns={columnasClientes}
            getRowId={(r) => r.cliente}
            loading={cargandoClientes}
            rowHeight={46}
            onRowDoubleClick={(p) => abrirCliente(p.row)}
            initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
            pageSizeOptions={[25, 50, 100]}
            slots={{ noRowsOverlay: SinClientes }}
            sx={fpDataGridSx}
            disableRowSelectionOnClick
          />
        </Box>
      </Box>
    );
  }

  // ---- Vista: líneas del cliente ----
  return (
    <>
      <Box sx={sectionBox}>
        <Stack
          direction="row"
          spacing={1.4}
          alignItems="center"
          sx={{ flexWrap: "wrap", rowGap: 1.2 }}
        >
          <Button startIcon={<ArrowBackIcon />} onClick={volver} sx={btnOutline(FP.color.navy)}>
            Clientes
          </Button>
          <Box sx={{ flex: 1, minWidth: 220 }}>
            <Typography sx={{ fontWeight: 800, fontSize: "1rem" }}>
              {cliente.clientenombre}
            </Typography>
            <Typography sx={{ fontSize: "0.78rem", color: FP.color.textSecondary }}>
              {conduces.length} conduce(s) · {enPisoTotal} en piso · {vendidosTotal} vendidos sin
              facturar
            </Typography>
          </Box>
          {puedeModificar && (
            <Button
              startIcon={<ReceiptLongOutlinedIcon />}
              onClick={abrirFacturar}
              disabled={!seleccionadas.length}
              sx={btnSolid(FP.color.navy, FP.color.navySoft)}
            >
              Vender y facturar ({seleccionadas.length})
            </Button>
          )}
        </Stack>
      </Box>

      <Box sx={{ ...sectionBox, mb: 0 }}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={1.5}
          alignItems={{ md: "center" }}
          sx={{ mb: 2 }}
        >
          <Stack direction="row" spacing={0.8} sx={{ flexWrap: "wrap", rowGap: 0.8, flex: 1 }}>
            <Chip
              label={`Todos (${lineas.length})`}
              onClick={() => setFiltroConduce(0)}
              color={filtroConduce ? "default" : "primary"}
              sx={{ fontWeight: 700 }}
            />
            {conduces.map((c) => (
              <Chip
                key={c.id}
                label={`#${c.id} · ${c.fecha} (${c.motores})`}
                onClick={() => setFiltroConduce(c.id)}
                color={filtroConduce === c.id ? "primary" : "default"}
                sx={{ fontWeight: 700 }}
              />
            ))}
          </Stack>
          <TextField
            size="small"
            placeholder="Buscar o escanear chasis"
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                alEnter();
              }
            }}
            autoComplete="off"
            inputProps={{ autoCapitalize: "characters", autoCorrect: "off", spellCheck: false }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <QrCodeScannerIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ ...fpInputSx, minWidth: 260 }}
          />
        </Stack>

        <Box sx={{ bgcolor: "#fff" }}>
          <DataGrid
            autoHeight
            rows={visibles}
            columns={columnasLineas}
            getRowId={(r) => r.chasis}
            loading={cargandoLineas}
            rowHeight={46}
            initialState={{ pagination: { paginationModel: { pageSize: 50 } } }}
            pageSizeOptions={[50, 100, 200]}
            slots={{ noRowsOverlay: SinLineas }}
            sx={fpDataGridSx}
            disableRowSelectionOnClick
          />
        </Box>
        <Typography sx={{ mt: 1.2, fontSize: "0.76rem", color: FP.color.textMuted }}>
          Seleccionados: {seleccionadas.length} ({selEnPiso} en piso, que quedarán vendidos al
          facturar). La casilla del encabezado marca todo lo visible con el filtro actual. Enter en
          el buscador agrega el chasis escaneado a la selección.
        </Typography>
      </Box>

      {/* Vender y facturar */}
      <Dialog
        open={factOpen}
        onClose={() => !facturando && setFactOpen(false)}
        fullWidth
        maxWidth="md"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 800, color: FP.color.navy }}>
          Vender y facturar · {cliente.clientenombre}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                label="Tipo de Comprobante"
                value={tipocomprobante}
                onChange={(e) => setTipocomprobante(e.target.value)}
                helperText={
                  config && !config.ncfdisponible
                    ? "NCF no configurado — se emitirá sin comprobante fiscal"
                    : undefined
                }
                sx={fpInputSx}
              >
                {(config?.tiposComprobante || []).map((t) => (
                  <MenuItem key={t} value={t}>
                    {t}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                select
                fullWidth
                label="Condición"
                value={tipofactura}
                onChange={(e) => setTipofactura(e.target.value)}
                sx={fpInputSx}
              >
                <MenuItem value="Contado">Contado</MenuItem>
                <MenuItem value="Credito">Credito</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                fullWidth
                label="Tasa"
                value={tasa}
                onChange={(e) => setTasa(e.target.value)}
                inputProps={{ style: { textAlign: "right" }, inputMode: "decimal" }}
                sx={fpInputSx}
              />
            </Grid>
            {selEnPiso > 0 && (
              <Grid item xs={6} sm={4}>
                <TextField
                  fullWidth
                  type="date"
                  label="Fecha de venta"
                  InputLabelProps={{ shrink: true }}
                  value={fechaventa}
                  onChange={(e) => setFechaventa(e.target.value)}
                  helperText={`Para los ${selEnPiso} que estaban en piso`}
                  sx={fpInputSx}
                />
              </Grid>
            )}
            {tipofactura === "Credito" && (
              <>
                <Grid item xs={6} sm={3}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Plazos"
                    value={plazos}
                    onChange={(e) => setPlazos(e.target.value)}
                    inputProps={{ min: 1, max: 60 }}
                    error={!plazosValidos}
                    helperText="1 a 60 cuotas"
                    sx={fpInputSx}
                  />
                </Grid>
                <Grid item xs={6} sm={4}>
                  <TextField
                    fullWidth
                    type="date"
                    label="Fecha primer pago"
                    InputLabelProps={{ shrink: true }}
                    value={fechaprimerpago}
                    onChange={(e) => setFechaprimerpago(e.target.value)}
                    sx={fpInputSx}
                  />
                </Grid>
              </>
            )}

            {grupos.length > 1 && (
              <Grid item xs={12}>
                <Box
                  sx={{
                    border: "1px solid #FFB74D",
                    bgcolor: "#FFF8E1",
                    borderRadius: 2,
                    px: 2,
                    py: 1.2,
                  }}
                >
                  <Typography sx={{ fontSize: "0.82rem", fontWeight: 700 }}>
                    Se emitirán {grupos.length} facturas: una por cada{" "}
                    {variasEmpresas ? "empresa y moneda" : "moneda"}.
                  </Typography>
                </Box>
              </Grid>
            )}

            {grupos.map((g, i) => {
              const mon = monedaLabel(g.moneda);
              const cuota =
                tipofactura === "Credito" && nPlazos > 0 ? round2(g.total / nPlazos) : 0;
              return (
                <Grid item xs={12} key={g.clave}>
                  <Box sx={{ border: `1px solid ${FP.color.border}`, borderRadius: 2, p: 1.6 }}>
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={1}
                      sx={{ mb: 1.2, flexWrap: "wrap", rowGap: 0.8 }}
                    >
                      <Typography sx={{ fontWeight: 800, fontSize: "0.84rem", flex: 1 }}>
                        {grupos.length > 1 ? `Factura ${i + 1} · ` : ""}
                        {mon}
                        {variasEmpresas ? ` · Empresa ${g.empresa}` : ""}
                        <Typography
                          component="span"
                          sx={{ fontSize: "0.74rem", color: FP.color.textMuted, ml: 1 }}
                        >
                          {g.conduces.length > 1 ? "Conduces " : "Conduce "}
                          {g.conduces.map((c) => `#${c}`).join(", ")}
                        </Typography>
                      </Typography>
                      <Chip
                        label={`${g.lineas.length} chasis · ${mon} ${fmtMoney(g.total)}${
                          cuota ? ` · ${nPlazos} x ${fmtMoney(cuota)}` : ""
                        }`}
                        sx={{ bgcolor: FP.color.surfaceAlt, fontWeight: 700 }}
                      />
                    </Stack>
                    <Stack spacing={1}>
                      {g.lineas.map((l) => {
                        const min = minimo(l);
                        const bajo = min > 0 && toNum(factPrecios[l.chasis]) < min;
                        return (
                          <Stack
                            key={l.chasis}
                            direction="row"
                            spacing={1.2}
                            alignItems="center"
                            sx={{ flexWrap: "wrap", rowGap: 0.6 }}
                          >
                            <Box sx={{ minWidth: 240, flex: 1 }}>
                              <Typography sx={{ fontWeight: 700, fontSize: "0.84rem" }}>
                                {l.chasis}
                              </Typography>
                              <Typography sx={{ fontSize: "0.72rem", color: FP.color.textMuted }}>
                                {[l.marca, l.modelo, l.colore].filter(Boolean).join(" ")} · conduce
                                #{l.conduce}
                                {l.estado === "V"
                                  ? ` · vendido ${l.fechaventa || ""}`
                                  : " · en piso"}
                              </Typography>
                            </Box>
                            <TextField
                              size="small"
                              label="Precio"
                              value={factPrecios[l.chasis] ?? ""}
                              onChange={(e) =>
                                setFactPrecios((prev) => ({ ...prev, [l.chasis]: e.target.value }))
                              }
                              error={bajo}
                              helperText={min > 0 ? `Mínimo ${fmtMoney(min)}` : undefined}
                              inputProps={{ style: { textAlign: "right" }, inputMode: "decimal" }}
                              sx={{ ...fpInputSx, width: 160 }}
                            />
                          </Stack>
                        );
                      })}
                    </Stack>
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setFactOpen(false)} disabled={facturando} sx={btnGhost}>
            Cancelar
          </Button>
          <Button
            startIcon={
              facturando ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <ReceiptLongOutlinedIcon />
              )
            }
            onClick={facturar}
            disabled={
              facturando || !seleccionadas.length || !!bajoMinimo || !plazosValidos || !tasa
            }
            sx={btnSolid(FP.color.navy, FP.color.navySoft)}
          >
            {grupos.length > 1 ? `Emitir ${grupos.length} facturas` : "Emitir factura"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Facturas emitidas */}
      <Dialog
        open={!!resultado}
        onClose={() => setResultado(null)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 800, color: FP.color.navy }}>
          {(resultado?.facturas || []).length > 1 ? "Facturas emitidas" : "Factura emitida"}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={1.2}>
            {(resultado?.facturas || []).map((f) => (
              <Stack
                key={f.id}
                direction="row"
                spacing={1.2}
                alignItems="center"
                sx={{ border: `1px solid ${FP.color.border}`, borderRadius: 2, p: 1.4 }}
              >
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontWeight: 800, fontSize: "0.9rem" }}>
                    Factura #{f.id} · {monedaLabel(f.moneda)} {fmtMoney(f.total)}
                  </Typography>
                  <Typography sx={{ fontSize: "0.74rem", color: FP.color.textMuted }}>
                    {f.chasis.length} chasis · {f.conduces.length > 1 ? "conduces " : "conduce "}
                    {f.conduces.map((c) => `#${c}`).join(", ")}
                    {f.ncf ? ` · NCF ${f.ncf}` : ""}
                  </Typography>
                </Box>
                <Button
                  size="small"
                  startIcon={<VisibilityOutlinedIcon />}
                  onClick={() => setFacturaId(f.id)}
                  sx={btnOutline(FP.color.accentBlue)}
                >
                  Ver / imprimir
                </Button>
              </Stack>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setResultado(null)} sx={btnGhost}>
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Detalle de una factura emitida: la pantalla de siempre */}
      <Dialog fullScreen open={!!facturaId} onClose={() => setFacturaId(null)}>
        {facturaId && (
          <FacturaDetalleXmayor
            facturaId={facturaId}
            onExit={() => {
              setFacturaId(null);
              cargarLineas();
            }}
            onChanged={() => cargarLineas()}
          />
        )}
      </Dialog>
    </>
  );
}
